import { Container, Sprite, Text, Texture } from 'pixi.js';
import { BoardPanel } from '../board/BoardPanel';
import { TitlePlaque } from '../board/TitlePlaque';
import { animate, COLOURS, displayStyle, ease, prefersReducedMotion, uiStyle } from '../board/ui';
import { bulbTexture, glowTexture } from '../road/glow';

/** The bonus's name, in one place (producer 2026-10-14: names live in one constant). */
export const HOLD_AND_SPIN_NAME = 'Hold & Spin';

/** Bulb tints, as the side lights use them: warm brown when spent. */
const SPENT = 0x6e4520;
const LIT = 0xffe9a0;
const LIT_GLOW = 0xffb93d;
/** How bright a lit lamp's halo is. */
const GLOW = 0.45;
/** The bezel art's hole is 71% of its width; the bulb tucks just under it. */
const BULB_OF_BEZEL = 0.74;

interface Lamp {
  /** A dark bulb, showing when the spin is spent. */
  bulb: Sprite;
  /** The painted lit lens, over the bulb while the spin is left. */
  lens: Sprite;
  bezel: Sprite;
  glow: Sprite;
  lit: boolean;
}

/** The art the board draws with, looked up again until it has loaded. */
export interface HoldAndSpinBoardArt {
  floor: () => Texture | null;
  bezel: () => Texture | null;
  /** The lit lamp's painted lens. */
  lens: () => Texture | null;
  coin: () => Texture | null;
}

/**
 * The hold-and-spin bonus's board above the reels (producer 2026-10-14:
 * anything above the reels is a board, no pills): its name on a plaque,
 * the spins left as brass lamps that go out one by one and all light
 * again when a coin lands, and what the locked coins add up to.
 */
export class HoldAndSpinBoard extends Container {
  private readonly board = new BoardPanel();
  private readonly plaque = new TitlePlaque(HOLD_AND_SPIN_NAME.toUpperCase(), 15);
  private readonly spinsLabel: Text;
  private readonly wonLabel: Text;
  private readonly total: Text;
  private readonly coin = new Sprite();
  private readonly lamps: Lamp[] = [];
  private shownTotal = 0;
  private spins = 0;
  private w = 300;
  private h = 80;
  private countTo = 0;

  constructor(private readonly art: HoldAndSpinBoardArt, lampCount = 3) {
    super();
    this.spinsLabel = new Text({ text: 'SPINS LEFT', style: uiStyle(13, COLOURS.cream, '600') });
    this.spinsLabel.anchor.set(0.5, 0);
    this.wonLabel = new Text({ text: 'COINS LOCKED', style: uiStyle(13, COLOURS.cream, '600') });
    this.wonLabel.anchor.set(0.5, 0);
    this.total = new Text({ text: '0', style: displayStyle(26) });
    this.total.anchor.set(0, 0.5);
    this.coin.anchor.set(0.5);
    this.addChild(this.board, this.spinsLabel, this.wonLabel, this.coin, this.total);
    for (let i = 0; i < lampCount; i++) {
      const glow = new Sprite(glowTexture());
      const bulb = new Sprite(bulbTexture());
      const lens = new Sprite();
      const bezel = new Sprite();
      for (const s of [glow, bulb, lens, bezel]) s.anchor.set(0.5);
      glow.blendMode = 'add';
      glow.tint = LIT_GLOW;
      bulb.tint = SPENT;
      bezel.tint = 0xf2e2c0;
      this.addChild(glow, bulb, lens, bezel);
      this.lamps.push({ bulb, lens, bezel, glow, lit: false });
    }
    this.addChild(this.plaque);
    this.visible = false;
  }

  /** Spins left and coins locked, as the browser tests read them. */
  get snapshot(): { visible: boolean; spinsLeft: number; total: number } {
    return { visible: this.visible, spinsLeft: this.spins, total: this.countTo };
  }

  /** Place the board: its top edge (the plaque rides on it), size. */
  setLayout(x: number, y: number, width: number, height: number): void {
    this.position.set(Math.round(x), Math.round(y));
    this.w = Math.round(width);
    this.h = Math.round(height);
    this.redraw();
  }

  show(spins: number, total: number): void {
    this.visible = true;
    this.alpha = 1;
    this.spins = spins;
    this.shownTotal = total;
    this.countTo = total;
    this.total.text = total.toLocaleString('en-GB');
    this.lamps.forEach((lamp, i) => this.setLamp(lamp, i < spins, false));
    this.redraw();
    if (!prefersReducedMotion()) {
      this.alpha = 0;
      void animate(240, (t) => (this.alpha = t));
    }
  }

  hide(): void {
    if (!this.visible) return;
    void animate(200, (t) => (this.alpha = 1 - t)).then(() => {
      this.visible = false;
      this.alpha = 1;
    });
  }

  /** A spin was used, or coins landed and the lamps light again. */
  setSpins(spins: number): void {
    const relit = spins > this.spins;
    this.spins = spins;
    this.lamps.forEach((lamp, i) => this.setLamp(lamp, i < spins, relit));
  }

  /** Count the locked coins up to `total`. */
  setTotal(total: number): void {
    const from = this.shownTotal;
    this.countTo = total;
    if (total === from) return;
    void animate(Math.min(900, 300 + Math.abs(total - from) / 4), (t) => {
      const value = Math.round(from + (total - from) * ease.outCubic(t));
      this.shownTotal = value;
      this.total.text = value.toLocaleString('en-GB');
      this.placeTotal();
    }).then(() => {
      this.total.text = total.toLocaleString('en-GB');
      this.shownTotal = total;
      this.placeTotal();
    });
    if (!prefersReducedMotion()) {
      void animate(320, (t) => this.total.scale.set(1 + 0.18 * Math.sin(t * Math.PI)));
    }
  }

  /** The middle of the total, for coins to fly from. */
  get totalCentre(): { x: number; y: number } {
    return this.toGlobal({ x: this.total.x + this.total.width / 2, y: this.total.y });
  }

  private setLamp(lamp: Lamp, lit: boolean, flash: boolean): void {
    const was = lamp.lit;
    lamp.lit = lit;
    // Without the lens art yet, the bulb lights instead.
    const lens = lamp.lens.texture !== Texture.EMPTY;
    lamp.bulb.tint = lit && !lens ? LIT : SPENT;
    lamp.lens.alpha = lit ? 1 : 0;
    lamp.glow.alpha = lit ? GLOW : 0;
    if (prefersReducedMotion()) return;
    if (lit && (flash || !was)) {
      void animate(420, (t) => {
        if (!lamp.lit) return;
        lamp.lens.alpha = Math.min(1, t * 2);
        lamp.glow.alpha = GLOW + 0.5 * Math.sin(t * Math.PI);
      });
    } else if (!lit && was) {
      // A spent lamp fades out rather than snapping off.
      void animate(360, (t) => {
        if (lamp.lit) return;
        lamp.lens.alpha = 1 - t;
        lamp.glow.alpha = GLOW * (1 - t);
      });
    }
  }

  private redraw(): void {
    const { w, h } = this;
    this.board.setFloor(this.art.floor());
    this.board.resize(w, h);
    this.plaque.position.set(w / 2, 0);
    const top = this.plaque.plateHeight / 2 + 2;
    const room = h - top;
    const lamp = Math.round(Math.max(24, Math.min(52, room * 0.42, w * 0.09)));
    const labelSize = Math.round(Math.max(12, Math.min(16, lamp * 0.38)));
    this.spinsLabel.style = uiStyle(labelSize, COLOURS.cream, '600');
    this.wonLabel.style = uiStyle(labelSize, COLOURS.cream, '600');
    this.total.style = displayStyle(Math.round(Math.max(22, Math.min(40, lamp * 0.8))));
    const block = this.spinsLabel.height + 6 + lamp;
    const y0 = top + Math.max(4, (room - block) / 2);
    const lampY = y0 + this.spinsLabel.height + 6 + lamp / 2;

    // Spins left on the left half, coins locked on the right.
    const leftX = w * 0.28;
    this.spinsLabel.position.set(leftX, y0);
    const gap = lamp * 1.25;
    const bezel = this.art.bezel();
    const lens = this.art.lens();
    this.lamps.forEach((l, i) => {
      const x = leftX + (i - (this.lamps.length - 1) / 2) * gap;
      if (bezel) l.bezel.texture = bezel;
      l.bezel.visible = bezel !== null;
      l.bezel.width = l.bezel.height = lamp;
      l.bulb.width = l.bulb.height = lamp * (bezel ? BULB_OF_BEZEL : 0.8);
      if (lens && l.lens.texture !== lens) {
        l.lens.texture = lens;
        this.setLamp(l, l.lit, false);
      }
      l.lens.width = l.lens.height = lamp * 0.98;
      l.glow.width = l.glow.height = lamp * 2;
      for (const s of [l.bulb, l.lens, l.bezel, l.glow]) s.position.set(x, lampY);
    });
    this.wonLabel.position.set(w * 0.72, y0);
    const coin = this.art.coin();
    if (coin) this.coin.texture = coin;
    this.coin.visible = coin !== null;
    this.coin.width = this.coin.height = lamp * 0.9;
    this.total.y = lampY;
    this.placeTotal();
  }

  /** Centre the coin and the number together under their label. */
  private placeTotal(): void {
    const coinW = this.coin.visible ? this.coin.width + 6 : 0;
    const width = coinW + this.total.width;
    const x = this.w * 0.72 - width / 2;
    this.coin.position.set(x + this.coin.width / 2, this.total.y);
    this.total.x = x + coinW;
  }
}
